import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import mesaLogo from '@/assets/mesa_logo_light_circle.webp';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { broadcastAuthChange } from '@/lib/auth-broadcast';
import { authClient } from '@/lib/auth-client';
import { sanitizeRedirect } from '@/lib/redirects';
import { requestPasswordResetServerFn, signInServerFn, signUpServerFn } from '@/server/functions/auth';

const searchSchema = z.object({
  redirect: z.string().optional(),
  error: z.string().optional(),
});

const ERROR_MESSAGES: Record<string, { heading: string; message: string }> = {
  INVITATION_EXPIRED: {
    heading: 'Invitation expired',
    message: 'This invitation to Mesa has expired. Please ask the organization owner to send a new one.',
  },
  INVITATION_NOT_FOUND: {
    heading: 'Invitation required',
    message: 'Mesa signups are currently invite-only. Please ask your organization admin for an invitation.',
  },
};

export const Route = createFileRoute('/(auth)/sign-in')({
  validateSearch: searchSchema,
  component: SignIn,
});

type AuthMode = 'sign-in' | 'sign-up' | 'forgot-password';

function SignIn() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { redirect: redirectParam, error: errorParam } = Route.useSearch();
  const blockingError = errorParam ? ERROR_MESSAGES[errorParam] : undefined;
  const [mode, setMode] = useState<AuthMode>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const redirectPath = sanitizeRedirect(redirectParam);
  const webUrl = import.meta.env.VITE_WEB_URL;
  const redirectUrl = webUrl ? new URL(redirectPath, webUrl).toString() : null;

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      if (mode === 'forgot-password') {
        await requestPasswordResetServerFn({ data: { email } });
        setSuccess('Check your email for a password reset link.');
        return;
      }

      if (mode === 'sign-up') {
        await signUpServerFn({ data: { email, password, name } });
      } else {
        await signInServerFn({ data: { email, password } });
      }
      queryClient.invalidateQueries({ queryKey: ['auth-session'] });
      queryClient.invalidateQueries({ queryKey: ['auth-orgs'] });
      broadcastAuthChange();
      navigate({ to: redirectPath });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      if (!redirectUrl) {
        setError('VITE_WEB_URL is required');
        setGoogleLoading(false);
        return;
      }
      await authClient.signIn.social({
        provider: 'google',
        callbackURL: redirectUrl,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign in failed');
      setGoogleLoading(false);
    }
  };

  if (blockingError) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="h-2 bg-mesa-green" />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md border border-mesa-border bg-mesa-card p-8 text-center">
            <img src={mesaLogo} alt="Mesa" className="mx-auto mb-4 h-12 w-12" />
            <h1 className="text-xl font-semibold mb-2">{blockingError.heading}</h1>
            <p className="text-mesa-muted">{blockingError.message}</p>
          </div>
        </div>
        <div className="h-2 bg-mesa-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top green stripe */}
      <div className="h-2 bg-mesa-green" />

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md border border-mesa-border bg-mesa-card p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <img src={mesaLogo} alt="Mesa" className="mx-auto mb-4 h-12 w-12" />
            <h1 className="text-2xl font-semibold text-mesa-text tracking-tight">
              {mode === 'sign-up' && 'Create your account'}
              {mode === 'sign-in' && 'Sign in to Mesa'}
              {mode === 'forgot-password' && 'Reset your password'}
            </h1>
            {mode === 'forgot-password' && (
              <p className="mt-2 text-sm text-mesa-muted">Enter your email and we'll send you a reset link.</p>
            )}
          </div>

          {mode !== 'forgot-password' && (
            <div className="space-y-4">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGoogleSignIn}
                disabled={googleLoading}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {googleLoading ? 'Signing in...' : 'Continue with Google'}
              </Button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-mesa-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-mesa-card px-2 text-mesa-muted">Or continue with email</span>
                </div>
              </div>
            </div>
          )}

          {/* Email form */}
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            {error && <div className="border border-mesa-red/30 bg-mesa-red/10 p-3 text-sm text-mesa-red">{error}</div>}
            {success && (
              <div className="border border-mesa-green/30 bg-mesa-green/10 p-3 text-sm text-mesa-green">{success}</div>
            )}

            {mode === 'sign-up' && (
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Your name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {mode !== 'forgot-password' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  {mode === 'sign-in' && (
                    <button
                      type="button"
                      onClick={() => switchMode('forgot-password')}
                      className="text-xs text-mesa-muted hover:text-mesa-green"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Enter your password"
                  autoComplete={mode === 'sign-up' ? 'new-password' : 'current-password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading && 'Loading...'}
              {!loading && mode === 'sign-in' && 'Sign in'}
              {!loading && mode === 'sign-up' && 'Create account'}
              {!loading && mode === 'forgot-password' && 'Send reset link'}
            </Button>

            <div className="text-center text-sm text-mesa-muted">
              {mode === 'sign-up' && (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('sign-in')}
                    className="text-mesa-green hover:underline"
                  >
                    Sign in
                  </button>
                </>
              )}
              {mode === 'sign-in' && (
                <>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('sign-up')}
                    className="text-mesa-green hover:underline"
                  >
                    Sign up
                  </button>
                </>
              )}
              {mode === 'forgot-password' && (
                <>
                  Remember your password?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('sign-in')}
                    className="text-mesa-green hover:underline"
                  >
                    Sign in
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Bottom green stripe */}
      <div className="h-2 bg-mesa-green" />
    </div>
  );
}
